import dayjs from 'dayjs';
import {
  SortDirection,
  type TApiReadyCovenant,
  type TApiReadyCovenantDocument,
  type TApiSupervisorSegment,
} from '~/modules/covenants/types.ts';
import {
  getManagerApplicationDocumentStatusConfig,
  type IStatusConfig,
} from '~/modules/covenants/statusConfig.ts';
import {
  MANAGER_READY_COVENANT_COPIED_TOOLTIP_TEXT,
  MANAGER_READY_COVENANT_COPY_TOOLTIP_TEXT,
  MANAGER_READY_COVENANTS_EMPTY_VALUE,
  MANAGER_UK_LOCALE,
  MANAGER_UK_LOCALE_COMPARE_OPTIONS,
  EXPORT_DATE_FORMAT,
  EXPORT_FALLBACK_EXTENSION,
  EXPORT_FILENAME_PREFIX,
  READY_COVENANT_STATUS_CONFIGS,
  READY_COVENANT_TARGET_STATUS_CONFIGS,
  READY_DOCUMENT_STATUS_FALLBACK_CONFIGS,
} from '~/modules/covenants/CovenantsDataForm/CovenantsFormByManager/constants.ts';
import type {
  TManagerApplicationAssigneeFilter,
  TManagerApplicationDecision,
  TManagerApplicationRow,
  TManagerApplicationSegmentFilter,
  TManagerApplicationSortKey,
  TReadyCovenantTargetStatusVariant,
} from '~/modules/covenants/CovenantsDataForm/CovenantsFormByManager/types.ts';
import type { TManagerApplicationStatusFilter } from '~/modules/covenants/CovenantsDataForm/CovenantsFormByManager/types.ts';
import type { TSupervisorApplicationsRequest } from '~/store/covenants/types.ts';


export interface IReadyCovenantTargetStatusConfig extends IStatusConfig {
  variant: TReadyCovenantTargetStatusVariant;
}

export const getManagerAssigneeOptions = (applications: TManagerApplicationRow[]) =>
  [...new Set(applications.map((application) => application.assignee).filter(Boolean))].sort(
    (left, right) =>
      left.localeCompare(right, MANAGER_UK_LOCALE, MANAGER_UK_LOCALE_COMPARE_OPTIONS)
  );

export const buildManagerSegmentOptions = (segments: TApiSupervisorSegment[]) =>
  segments
    .map(({ segment }) => ({
      label: segment.displayName || segment.id,
      value: segment.id,
    }))
    .filter(
      (segmentOption, index, options) =>
        !!segmentOption.value &&
        options.findIndex((option) => option.value === segmentOption.value) === index
    )
    .sort((left, right) =>
      left.label.localeCompare(right.label, MANAGER_UK_LOCALE, MANAGER_UK_LOCALE_COMPARE_OPTIONS)
    );

export const getFilenameExtension = (filename?: string): string | null => {
  if (!filename) {
    return null;
  }

  const normalizedFileName = filename.trim();
  const extensionIndex = normalizedFileName.lastIndexOf('.');

  if (extensionIndex <= 0 || extensionIndex === normalizedFileName.length - 1) {
    return null;
  }

  return normalizedFileName.slice(extensionIndex);
};

export const normalizeContentType = (value: unknown): string | undefined =>
  typeof value === 'string' && value.trim() ? value : undefined;

export const buildExportFileName = (filename?: string): string => {
  const exportDate = dayjs().format(EXPORT_DATE_FORMAT);
  const extension = getFilenameExtension(filename) || EXPORT_FALLBACK_EXTENSION;

  return `${EXPORT_FILENAME_PREFIX}-${exportDate}${extension}`;
};

const normalizeManagerIdentityValue = (value: string) => value.trim().toLowerCase();

const buildManagerIdentityVariants = (value: string): string[] => {
  const normalizedValue = normalizeManagerIdentityValue(value);

  if (!normalizedValue) {
    return [];
  }

  const variants = new Set<string>([normalizedValue]);
  const slashSeparatedPart = normalizedValue.split(/[\\/]/).pop() ?? normalizedValue;
  const emailLocalPart = normalizedValue.split('@')[0] ?? normalizedValue;
  const slashPartEmailLocalPart = slashSeparatedPart.split('@')[0] ?? slashSeparatedPart;

  [slashSeparatedPart, emailLocalPart, slashPartEmailLocalPart].forEach((variant) => {
    const normalizedVariant = normalizeManagerIdentityValue(variant);

    if (normalizedVariant) {
      variants.add(normalizedVariant);
    }
  });

  return [...variants];
};

export const isManagerAssigneeMatch = (
  assignee: string | null | undefined,
  currentManagerIdentityValues: Array<string | null | undefined>
) => {
  if (!assignee || !currentManagerIdentityValues.length) {
    return false;
  }

  const assigneeVariants = new Set(buildManagerIdentityVariants(assignee));

  return currentManagerIdentityValues.some((identityValue) => {
    if (!identityValue) {
      return false;
    }

    return buildManagerIdentityVariants(identityValue).some((variant) => assigneeVariants.has(variant));
  });
};

export const getSortedManagerAssigneeOptions = (assignees: Array<string | null | undefined>) =>
  [...new Set(assignees.filter((assignee): assignee is string => !!assignee))].sort(
    (left, right) =>
      left.localeCompare(right, MANAGER_UK_LOCALE, MANAGER_UK_LOCALE_COMPARE_OPTIONS)
  );

export const getNextManagerSortState = (
  currentSortKey: TManagerApplicationSortKey | null,
  currentSortDirection: SortDirection,
  nextSortKey: TManagerApplicationSortKey
) => {
  if (currentSortKey !== nextSortKey) {
    return {
      sortKey: nextSortKey,
      sortDirection: SortDirection.Asc,
    };
  }

  if (currentSortDirection === SortDirection.Asc) {
    return {
      sortKey: nextSortKey,
      sortDirection: SortDirection.Desc,
    };
  }

  if (currentSortDirection === SortDirection.Desc) {
    return {
      sortKey: null,
      sortDirection: SortDirection.None,
    };
  }

  return {
    sortKey: nextSortKey,
    sortDirection: SortDirection.Asc,
  };
};

export const getManagerSortParam = (
  sortKey: TManagerApplicationSortKey | null,
  sortDirection: SortDirection
) => {
  if (!sortKey || sortDirection === SortDirection.None) {
    return undefined;
  }

  return [`${sortKey},${sortDirection}`];
};

export const getSupervisorApplicationsRequestKey = (
  request: TSupervisorApplicationsRequest
): string =>
  JSON.stringify({
    page: request.page,
    size: request.size,
    fromDate: request.fromDate,
    toDate: request.toDate,
    search: request.search ?? '',
    assignee: request.assignee ?? [],
    segmentId: request.segmentId ?? [],
    status: request.status ?? [],
    sort: request.sort ?? [],
  });

export const getManagerSortTooltipTitle = (isActive: boolean, direction: SortDirection): string => {
  if (!isActive || direction === SortDirection.None) {
    return 'Сортувати за зростанням';
  }

  if (direction === SortDirection.Asc) {
    return 'Змінити на спадання';
  }

  return 'Скинути сортування';
};

export const getManagerStatusFilterTooltipTitle = (statuses: TManagerApplicationStatusFilter) =>
  statuses.length ? 'Фільтр за статусом' : 'Фільтр за статусом активний';

export const getManagerAssigneeFilterTooltipTitle = (
  assignees: TManagerApplicationAssigneeFilter
) => (assignees.length ? 'Фільтр за виконавцем активний' : 'Фільтр за виконавцем');

export const getManagerSegmentFilterTooltipTitle = (
  segmentIds: TManagerApplicationSegmentFilter
) => (segmentIds.length ? 'Фільтр за сегментом активний' : 'Фільтр за сегментом');

export const getReadyCovenantCopyTooltipTitle = (
  copiedCovenantKey: string | null,
  covenantKey: string
): string =>
  copiedCovenantKey === covenantKey
    ? MANAGER_READY_COVENANT_COPIED_TOOLTIP_TEXT
    : MANAGER_READY_COVENANT_COPY_TOOLTIP_TEXT;

export const toggleManagerDocumentDecision = (
  currentDecision: TManagerApplicationDecision,
  nextDecision: Exclude<TManagerApplicationDecision, null>
): TManagerApplicationDecision => (currentDecision === nextDecision ? null : nextDecision);

export const copyTextToClipboard = async (text: string): Promise<boolean> => {
  if (navigator?.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return true;
  }

  try {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    const copied = document.execCommand('copy');
    document.body.removeChild(textarea);
    return copied;
  } catch {
    return false;
  }
};

export const getReadyCovenantStatusConfig = (status?: string): IStatusConfig => {
  if (!status) {
    return {
      label: MANAGER_READY_COVENANTS_EMPTY_VALUE,
      color: 'default',
      description: '',
    };
  }

  const normalizedStatus = status.toUpperCase();

  return (
    READY_COVENANT_STATUS_CONFIGS[normalizedStatus] ?? {
      label: status,
      color: 'default',
      description: '',
    }
  );
};

export const getReadyDocumentStatusConfig = (status?: string): IStatusConfig => {
  if (!status) {
    return {
      label: MANAGER_READY_COVENANTS_EMPTY_VALUE,
      color: 'default',
      description: '',
    };
  }

  const normalizedStatus = status.toUpperCase();

  if (READY_DOCUMENT_STATUS_FALLBACK_CONFIGS[normalizedStatus]) {
    return READY_DOCUMENT_STATUS_FALLBACK_CONFIGS[normalizedStatus];
  }

  return getManagerApplicationDocumentStatusConfig(normalizedStatus as never);
};

export const formatReadyDocumentPeriod = (childPeriod?: string, yearPeriod?: string): string =>
  [childPeriod, yearPeriod].filter(Boolean).join(' ') || MANAGER_READY_COVENANTS_EMPTY_VALUE;

export const getReadyCovenantKey = (covenant: TApiReadyCovenant): string =>
  [
    covenant.grafId,
    covenant.yearPeriod?.trim() ?? '',
    covenant.childPeriod?.trim() ?? '',
    covenant.deadline?.trim() ?? '',
  ].join('|');

const normalizeReadyCovenantTargetStatus = (status: string): string =>
  status.trim().replace(/\s+/g, ' ').toLowerCase();

export const getReadyCovenantTargetStatusConfig = (
  status?: string
): IReadyCovenantTargetStatusConfig => {
  const trimmedStatus = status?.trim() ?? '';

  if (!trimmedStatus) {
    return {
      label: MANAGER_READY_COVENANTS_EMPTY_VALUE,
      color: 'default',
      description: '',
      variant: 'default',
    };
  }

  const normalizedStatus = normalizeReadyCovenantTargetStatus(trimmedStatus);

  return {
    label: trimmedStatus,
    ...(READY_COVENANT_TARGET_STATUS_CONFIGS[normalizedStatus] ?? {
      color: 'default',
      description: '',
      variant: 'default',
    }),
  };
};

export const getNextDocumentBatchIds = ({
  documentIdsGroup,
  loadedDocuments,
  batchSize,
}: {
  documentIdsGroup: number[];
  loadedDocuments: TApiReadyCovenantDocument[];
  batchSize: number;
}): number[] => {
  const loadedIds = new Set(loadedDocuments.map((document) => document.id));

  return documentIdsGroup.filter((documentId) => !loadedIds.has(documentId)).slice(0, batchSize);
};

export const mergeReadyCovenantDocuments = ({
  currentDocuments,
  fetchedDocuments,
  documentIdsGroup,
}: {
  currentDocuments: TApiReadyCovenantDocument[];
  fetchedDocuments: TApiReadyCovenantDocument[];
  documentIdsGroup: number[];
}): TApiReadyCovenantDocument[] => {
  const mergedById = new Map<number, TApiReadyCovenantDocument>();

  currentDocuments.forEach((document) => {
    mergedById.set(document.id, document);
  });
  fetchedDocuments.forEach((document) => {
    mergedById.set(document.id, document);
  });

  const orderedDocuments: TApiReadyCovenantDocument[] = [];
  const addedIds = new Set<number>();

  documentIdsGroup.forEach((documentId) => {
    const document = mergedById.get(documentId);
    if (!document) {
      return;
    }

    orderedDocuments.push(document);
    addedIds.add(document.id);
  });

  mergedById.forEach((document) => {
    if (addedIds.has(document.id)) {
      return;
    }

    orderedDocuments.push(document);
  });

  return orderedDocuments;
};
